import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { debounce, includes, isEqual } from 'lodash';

import { getRiders, updateRider } from '../api/services/Riders';
import * as Constants from '../constants';

export type GroupedOption = {
  group: string;
  label: string;
  id?: string;
  name?: string;
  age?: number;
  notes?: string;
  photo?: string;
  booster?: boolean;
  driverGenderPreferences?: string[];
  services?: {
    code: string;
    name: string;
  }[];
};

export interface UseRiderOptionsHook {
  savedRiders: GroupedOption[];
  ridersOnRide: GroupedOption[];
  deboucedQueryRiders: (query: string, state?: string | undefined, byRiderAlpha?: boolean) => void;
  addRiderToRide: (rider: GroupedOption) => void;
  removeRidersFromRide: (riderSlugs: string[]) => void;
  removeRiders: (slugs: string[]) => void;
  resetRiders: () => void;
  updateRiderNotes: (rider: Rider & { slug: string }) => void;
  newRemovedRiders?: RemovedRider[];
}

export const useRiderOptions = (rideDetails?: RideDetails): UseRiderOptionsHook => {
  const location = useLocation();
  const { riderSlug } = (location.state as { riderSlug?: string }) || {};
  const previousRiderSlugRef = useRef(riderSlug);
  const [riders, setRiders] = useState<RiderForList[]>([]);
  const [ridersOnRide, setRidersOnRide] = useState<GroupedOption[]>([]);
  const [savedRiders, setSavedRiders] = useState<GroupedOption[]>([]);
  const [newRemovedRiders, setNewRemovedRiders] = useState<RemovedRider[]>([]);

  useEffect(() => {
    const isNewRideTargetingDifferentRider =
      location.pathname.match('new$') && riderSlug && riderSlug !== previousRiderSlugRef.current;

    location.pathname.match('(new|edit)$') && fetchRiders();

    if (rideDetails) {
      const persistedRiders = rideDetails.waypoints.flatMap((waypoint) => {
        return waypoint.riders;
      });

      setRidersOnRide(ridersOnRide.filter((rider) => persistedRiders.some((r) => r.slug === rider.id)));
    } else if (isNewRideTargetingDifferentRider) {
      setRidersOnRide([]);
    }

    previousRiderSlugRef.current = riderSlug;
  }, [location.pathname, riderSlug]);

  useEffect(() => {
    if (rideDetails) {
      const currentRiderIds = ridersOnRide.map((r) => r.id);
      const nextRiders =
        rideDetails.waypoints.flatMap((waypoint) => {
          return waypoint.riders;
        }) || [];
      const nextRiderIds = nextRiders.map((r) => r.slug);

      // Only run this if the riders have changed
      !isEqual(currentRiderIds, nextRiderIds) && addInitialRidersToRide(nextRiders);
    }

    // we must check for window.location.pathname for when riders were added/removed during ride edit
    // and the user clicked Cancel button to return to the ride details page
  }, [rideDetails, window.location.pathname]);

  const addRiderToRide = async (rider) => {
    if (
      !rider ||
      includes(
        ridersOnRide.map((riderOnRide) => riderOnRide.id),
        rider.id
      )
    ) {
      return;
    }

    setRidersOnRide((previousRiders) =>
      previousRiders.concat({
        group: 'Riders on this Ride:',
        label: rider.name || '',
        id: rider.id,
        name: rider.name,
        age: rider.age,
        notes: rider.notes,
        photo: rider.photo,
        booster: rider.booster,
        driverGenderPreferences: rider.driverGenderPreferences,
        services: rider.services,
      } as GroupedOption)
    );
  };

  const removeRidersFromRide = (riderSlugs) => {
    setRidersOnRide((previousRiders) => previousRiders.filter((rider) => riderSlugs.indexOf(rider.id) === -1));
  };

  const removeRiders = (slugs) => {
    const ridersToRemove = ridersOnRide.filter((r) => slugs.indexOf(r.id) !== -1);
    const removedRiders = [
      ...newRemovedRiders,
      ...ridersToRemove.map((r) => ({ fullName: r.name || '', riderStatus: 'not_riding', slug: r.id || '' })),
    ];

    setNewRemovedRiders(removedRiders);
    setRidersOnRide((previousRiders) => previousRiders.filter((rider) => slugs.indexOf(rider.id) === -1));
  };

  const updateRiderNotes = async (rider) => {
    await updateRider(rider.slug, { riderNotes: rider.riderNotes });

    setRidersOnRide((previousRiders) =>
      previousRiders.map((previousRider) => {
        if (previousRider.id === rider.slug) {
          previousRider.notes = rider.riderNotes;
        }

        return previousRider;
      })
    );
  };

  const fetchRiders = useCallback(async (query?: string) => {
    if (query?.length && query.length < Constants.MINIMUM_SEARCH_STRING_LENGTH) {
      return;
    }
    const newRidersResponse = await getRiders({
      q: query,
      'include[]': ['rider_notes', 'driver_gender_preferences'],
      state: 'active',
    });

    if (newRidersResponse?.type === 'success') {
      setRiders(newRidersResponse.data.riders);
    }
  }, []);

  const deboucedQueryRiders = debounce(fetchRiders, 400);

  useEffect(() => {
    Array.isArray(riders) &&
      setSavedRiders(
        riders.map(
          (rider) =>
            ({
              group: 'Saved riders:',
              label: rider.fullName || '',
              id: rider.slug,
              name: rider.fullName,
              age: rider.age,
              notes: rider.riderNotes,
              photo: rider.profilePhotoUrl,
              booster: rider.safetySettings.boosterSeat,
              driverGenderPreferences: rider.driverGenderPreferences,
              services: rider.services,
            } as GroupedOption)
        )
      );
  }, [riders]);

  const addInitialRidersToRide = useCallback(
    async (currentRiders) => {
      setRidersOnRide(
        currentRiders.map((rider) => ({
          group: 'Riders on this Ride:',
          label: rider.fullName || '',
          id: rider.slug,
          name: rider.fullName,
          age: rider.age,
          notes: rider.riderNotes,
          photo: rider.profilePhoto ? rider.profilePhotoUrls?.small : '',
          driverGenderPreferences: rider.driverGenderPreferences,
          booster: rider.needsBooster,
          services: rider.services,
        }))
      );
    },
    [fetchRiders, setRidersOnRide]
  );

  return {
    deboucedQueryRiders,
    savedRiders,
    ridersOnRide,
    addRiderToRide,
    removeRidersFromRide,
    removeRiders,
    updateRiderNotes,
    resetRiders: () => setRidersOnRide([]),
    newRemovedRiders,
  };
};
